/**
 * @alfin-crm/contracts — Shared API DTOs
 *
 * Common pagination, filtering, and response envelope schemas.
 * Domain-specific DTOs live in each app's own module schemas.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
export const CursorPaginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(20),
});

export const OffsetPaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(20),
});

export type CursorPagination = z.infer<typeof CursorPaginationSchema>;
export type OffsetPagination = z.infer<typeof OffsetPaginationSchema>;

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------
export const SortSchema = z.object({
  sortBy: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});

export type Sort = z.infer<typeof SortSchema>;

// ---------------------------------------------------------------------------
// Date range filter
// ---------------------------------------------------------------------------
export const DateRangeSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export type DateRange = z.infer<typeof DateRangeSchema>;

// ---------------------------------------------------------------------------
// Common ID param
// ---------------------------------------------------------------------------
export const IdParamSchema = z.object({
  id: z.string().cuid('Invalid ID format'),
});

export type IdParam = z.infer<typeof IdParamSchema>;

// ---------------------------------------------------------------------------
// Status type enum (matches Prisma StatusType)
// ---------------------------------------------------------------------------
export const StatusTypeSchema = z.enum(['LOAN', 'OFFER', 'TRANSACTION', 'CAMPAIGN']);
export type StatusType = z.infer<typeof StatusTypeSchema>;

// ---------------------------------------------------------------------------
// Phone normalization helper (shared validation rule)
// ---------------------------------------------------------------------------
export const E164PhoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, 'Phone must be in E.164 format (e.g. +919876543210)');

// ---------------------------------------------------------------------------
// AI SSE event types
// ---------------------------------------------------------------------------
export const AiEventTypeSchema = z.enum([
  'status',
  'text_delta',
  'table',
  'chart',
  'clarification',
  'final',
  'error',
]);
export type AiEventType = z.infer<typeof AiEventTypeSchema>;

export const AiSseEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('status'), message: z.string() }),
  z.object({ type: z.literal('text_delta'), delta: z.string() }),
  z.object({
    type: z.literal('table'),
    columns: z.array(z.string()),
    rows: z.array(z.record(z.unknown())),
    rowCount: z.number().int(),
  }),
  z.object({
    type: z.literal('chart'),
    chartType: z.enum(['bar', 'line', 'pie', 'area', 'scatter']),
    title: z.string(),
    data: z.array(z.record(z.unknown())),
    xKey: z.string(),
    yKeys: z.array(z.string()),
  }),
  z.object({
    type: z.literal('clarification'),
    question: z.string(),
    suggestions: z.array(z.string()).optional(),
  }),
  z.object({
    type: z.literal('final'),
    sessionId: z.string(),
    queryLogId: z.string(),
    tokensUsed: z.object({ input: z.number().int(), output: z.number().int() }),
    durationMs: z.number().int(),
  }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);

export type AiSseEvent = z.infer<typeof AiSseEventSchema>;
